/**
 * Zentrale Area-Konfiguration: Route → PageContext, Turnier-Bereiche, UI-Optionen.
 * Area/route context derived from AppRegistry (ai.area) where possible; fallback for hub/non-app routes.
 */

import { FLOWS, getFlow } from "./flow.registry.js";
import {
  getAllApps,
  getAppByKey,
  resolveAppKeyForRoute,
} from "../../../modules/appRegistry/appRegistry.ts";
import { buildTrainingsDeskPath } from "@tennis-matches/core/constants/trainingsDesk.js";

/** Versendete/offene Rechnungen im TrainingsDesk (nicht Hub-Startseite). */
const TRAININGS_DESK_RECHNUNGEN_PATH = `${buildTrainingsDeskPath("rechnungen")}?archive=sent`;

/** Area → PageContext (labelKey, suggestionKeys, page). Used when route resolves to an app with ai.area. */
const AREA_CONTEXTS = {
  schleifchen: {
    area: "schleifchen",
    page: "turnier",
    labelKey: "ai.context.schleifchen.turnier",
    suggestionKeys: [
      "ai.suggestions.schleifchen.1",
      "ai.suggestions.schleifchen.2",
      "ai.suggestions.schleifchen.3",
    ],
  },
  matchmixer: {
    area: "schleifchen",
    page: "turnier",
    labelKey: "ai.context.schleifchen.turnier",
    suggestionKeys: [
      "ai.suggestions.schleifchen.1",
      "ai.suggestions.schleifchen.2",
      "ai.suggestions.schleifchen.3",
    ],
  },
  clubmeisterschaft: {
    area: "clubmeisterschaft",
    page: "turnier",
    labelKey: "ai.context.clubmeisterschaft.turnier",
    suggestionKeys: [
      "ai.suggestions.clubmeisterschaft.1",
      "ai.suggestions.clubmeisterschaft.2",
      "ai.suggestions.clubmeisterschaft.3",
    ],
  },
  trainer: {
    area: "trainer",
    page: "dashboard",
    labelKey: "ai.context.trainer.dashboard",
    suggestionKeys: [
      "ai.suggestions.trainer.dashboard.1",
      "ai.suggestions.trainer.dashboard.2",
      "ai.suggestions.trainer.dashboard.3",
    ],
  },
  coachdesk: {
    area: "coachdesk",
    page: "trainingsdesk",
    labelKey: "ai.context.coachdesk",
    suggestionKeys: [
      "ai.suggestions.coachdesk.1",
      "ai.suggestions.coachdesk.2",
      "ai.suggestions.coachdesk.3",
    ],
  },
  ltr: {
    area: "ltr",
    page: "tennis-live",
    labelKey: "ai.context.ltr",
    suggestionKeys: [
      "ai.suggestions.ltr.1",
      "ai.suggestions.ltr.2",
      "ai.suggestions.ltr.3",
    ],
  },
};

/** Non-app routes (e.g. hub). Minimal fallback list. */
const ROUTE_CONTEXTS = [
  {
    test: (r) => r === "hub-ki-assistant",
    context: {
      area: "hub",
      page: "ki-assistant",
      labelKey: "ai.context.kiAssistant",
      suggestionKeys: [
        "ai.suggestions.ki.1",
        "ai.suggestions.ki.2",
        "ai.suggestions.ki.3",
      ],
    },
  },
  {
    test: (r) => r === "hub" || r.startsWith("hub-"),
    context: {
      area: "hub",
      page: "home",
      labelKey: "ai.context.hub",
      suggestionKeys: [
        "ai.suggestions.hub.1",
        "ai.suggestions.hub.2",
        "ai.suggestions.hub.3",
      ],
    },
  },
];

const FALLBACK_CONTEXT = {
  area: "home",
  page: "home",
  labelKey: "ai.context.home",
  suggestionKeys: [
    "ai.suggestions.home.1",
    "ai.suggestions.home.2",
    "ai.suggestions.home.3",
  ],
};

/**
 * @param {string} route - Route or pathname (e.g. "schleifchen-setup", "/clubmeisterschaft")
 * @returns {{ area: string; page: string; labelKey: string; suggestionKeys: string[] }}
 */
export function getPageContext(route) {
  const r = String(route || "")
    .trim()
    .replace(/^\//, "");
  const appKey = resolveAppKeyForRoute(r);
  const app = appKey ? getAppByKey(appKey) : null;
  const area = app?.ai?.area;
  if (area && AREA_CONTEXTS[area]) return AREA_CONTEXTS[area];
  const entry = ROUTE_CONTEXTS.find((e) => e.test(r));
  return entry ? entry.context : FALLBACK_CONTEXT;
}

/** Bereiche mit eigenem Setup-Flow (für Modul-Auswahl im Chat). Aus AppRegistry + FLOWS abgeleitet. */
export const TOURNAMENT_AREAS = (() => {
  const apps = getAllApps();
  return apps
    .filter((a) => a.ai?.area && FLOWS[a.ai.area])
    .map((a) => a.ai.area);
})();

/**
 * @param {string} area
 * @returns {string | null} flowId oder null
 */
export function getFlowIdForArea(area) {
  const flow = FLOWS[area];
  return flow ? flow.id : null;
}

/**
 * UI-Optionen pro Area (Wizard-Button, Setup-Route). Erweiterbar für weitere Bereiche.
 * @type {Record<string, { hasWizardButton?: boolean; setupRoutePrefix?: string }>}
 */
export const AREA_UI = {
  schleifchen: { hasWizardButton: true, setupRoutePrefix: "schleifchen-setup" },
  clubmeisterschaft: {
    hasWizardButton: false,
    setupRoutePrefix: "clubmeisterschaft-setup",
  },
};

/**
 * Vorschlags-Items pro Area (id, labelKey, action). Wenn gesetzt, wird diese Liste genutzt;
 * sonst werden pageContext.suggestionKeys verwendet.
 * @type {Record<string, Array<{ id: string; labelKey: string; action: string }>>}
 */
export const AREA_SUGGESTION_ITEMS = {
  hub: [
    {
      id: "sug-hub-plan",
      labelKey: "ai.flow.common.planTournament",
      action: "send",
    },
    {
      id: "sug-hub-schleifchen",
      labelKey: "chat.suggest.openSchleifchen",
      action: "navigate",
      route: "schleifchen-setup",
    },
    {
      id: "sug-hub-trainer",
      labelKey: "chat.suggest.openTrainerstunden",
      action: "navigate",
      route: "trainerstunden",
    },
    {
      id: "sug-hub-club",
      labelKey: "chat.suggest.openClubmeisterschaft",
      action: "navigate",
      route: "clubmeisterschaft-setup",
    },
    {
      id: "sug-hub-ltr",
      labelKey: "chat.suggest.openTennisLive",
      action: "navigate",
      route: "ltr",
    },
  ],
  "ki-assistant": [
    { id: "sug-ki-training", labelKey: "ai.suggestions.ki.1", action: "send" },
    {
      id: "sug-ki-trainers",
      labelKey: "ai.suggestions.ki.trainers",
      action: "send",
    },
    {
      id: "sug-ki-coachdesk",
      labelKey: "ai.suggestions.coachdesk.1",
      action: "send",
    },
    {
      id: "sug-ki-open-td",
      labelKey: "chat.suggest.openTrainerstunden",
      action: "navigate",
      route: "trainerstunden",
    },
  ],
  trainer: [
    {
      id: "sug-trainer-plan",
      labelKey: "chat.suggest.planTraining",
      action: "send",
    },
    {
      id: "sug-trainer-schedule",
      labelKey: "chat.suggest.showSchedule",
      action: "send",
    },
  ],
  coachdesk: [
    {
      id: "sug-cd-lookup",
      labelKey: "ai.suggestions.coachdesk.1",
      action: "send",
    },
    {
      id: "sug-cd-invoice",
      labelKey: "ai.suggestions.coachdesk.2",
      action: "send",
    },
    {
      id: "sug-cd-plan",
      labelKey: "ai.suggestions.coachdesk.3",
      action: "send",
    },
  ],
  schleifchen: [
    {
      id: "sug-schleifchen-wizard",
      labelKey: "ai.chatWizard.start",
      action: "send",
    },
    {
      id: "sug-schleifchen-new",
      labelKey: "chat.suggest.newSchleifchen",
      action: "send",
    },
    {
      id: "sug-schleifchen-players",
      labelKey: "chat.suggest.enterPlayers",
      action: "send",
    },
    {
      id: "sug-schleifchen-schedule",
      labelKey: "ai.quickReply.schleifchen.schedule",
      action: "send",
    },
    {
      id: "sug-schleifchen-mode",
      labelKey: "ai.quickReply.schleifchen.changeMode",
      action: "send",
    },
  ],
};

/**
 * Vorschlags-Items für Empty-State (nutzt AREA_SUGGESTION_ITEMS wenn vorhanden).
 * @param {{ area?: string; page?: string; suggestionKeys?: string[] }} pageContext
 */
export function getEmptyStateSuggestions(pageContext) {
  if (
    pageContext?.page === "ki-assistant" &&
    AREA_SUGGESTION_ITEMS["ki-assistant"]
  ) {
    return AREA_SUGGESTION_ITEMS["ki-assistant"].slice(0, 4);
  }
  const area = String(pageContext?.area || "home");
  const fixed = AREA_SUGGESTION_ITEMS[area];
  if (fixed) return fixed.slice(0, 6);
  const keys = Array.isArray(pageContext?.suggestionKeys)
    ? pageContext.suggestionKeys
    : [];
  return keys.slice(0, 6).map((key, i) => ({
    id: `sug-fallback-${area}-${i}`,
    labelKey: key,
    action: "send",
  }));
}

/** @type {Array<{ id: string; labelKey: string; value: string }>} */
export const TRAINING_CONFIRM_QUICK_REPLIES = [
  {
    id: "train-confirm-yes",
    labelKey: "ai.training.confirm.yes",
    value: "__training_confirm_yes",
  },
  {
    id: "train-confirm-no",
    labelKey: "ai.training.confirm.no",
    value: "__training_confirm_no",
  },
];

/** @type {Array<{ id: string; labelKey: string; value: string }>} */
export const PAYMENT_KNOWS_NAME_QUICK_REPLIES = [
  {
    id: "payment-knows-yes",
    labelKey: "ai.coachdesk.paymentKnowsName.yes",
    value: "__payment_knows_name_yes",
  },
  {
    id: "payment-knows-no",
    labelKey: "ai.coachdesk.paymentKnowsName.no",
    value: "__payment_knows_name_no",
  },
];

/** @type {Array<{ id: string; labelKey: string; action: "navigate"; route: string }>} */
export const TRAINING_RESULT_QUICK_REPLIES = [
  {
    id: "train-open-td",
    labelKey: "chat.suggest.openTrainerstunden",
    action: "navigate",
    route: "trainerstunden",
  },
];

/**
 * @param {Array<{ id?: string, name: string }>} people
 */
export function buildPersonPickQuickReplies(people) {
  return (Array.isArray(people) ? people : [])
    .slice(0, 4)
    .filter((p) => asStr(p?.name))
    .map((p, i) => ({
      id: `pick-person-${asStr(p.id) || i}`,
      label: asStr(p.name),
      value: asStr(p.name),
    }));
}

function asStr(v) {
  return String(v ?? "").trim();
}

function isInvoiceCoachDeskMessage(assistantMsg, tools) {
  if (assistantMsg?.coachDeskTopic === "invoices") return true;
  if (assistantMsg?.kind === "coachdesk-invoices") return true;
  return tools.some((tc) => {
    if (tc?.name === "coachdesk.listOpenInvoices") return true;
    if (tc?.name !== "coachdesk.lookupPersonStatus") return false;
    const scopes = tc?.args?.scopes;
    return Array.isArray(scopes) && scopes.includes("invoices");
  });
}

/**
 * Kontextuelle Quick-Replies nach Assistenten-Nachricht (Phase 1 TrainingsDesk).
 * @param {{ pendingTrainingConfirm?: object, ambiguousPeople?: Array<{ name: string }>, toolCalls?: Array<{ name: string }> } | null} assistantMsg
 */
export function getContextualQuickReplies(assistantMsg) {
  if (!assistantMsg) return [];
  if (assistantMsg.kind === "coachdesk-ask-payment-knows") {
    return [...PAYMENT_KNOWS_NAME_QUICK_REPLIES];
  }
  if (assistantMsg.pendingTrainingConfirm)
    return [...TRAINING_CONFIRM_QUICK_REPLIES];
  if (
    Array.isArray(assistantMsg.ambiguousPeople) &&
    assistantMsg.ambiguousPeople.length
  ) {
    return buildPersonPickQuickReplies(assistantMsg.ambiguousPeople);
  }
  const tools = Array.isArray(assistantMsg.toolCalls)
    ? assistantMsg.toolCalls
    : [];
  if (assistantMsg.kind === "help" || asStr(assistantMsg.helpHref)) {
    const href = asStr(assistantMsg.helpHref) || "/trainingsdesk/hilfe";
    return [
      {
        id: "open-help-doc",
        labelKey: "chat.suggest.openHelp",
        action: "navigate",
        route: href,
      },
    ];
  }
  if (
    tools.some(
      (tc) =>
        tc?.name === "training.searchSessions" ||
        tc?.name === "training.listWeekSessions",
    )
  ) {
    return [...TRAINING_RESULT_QUICK_REPLIES];
  }
  if (tools.some((tc) => tc?.name === "training.listTrainers")) {
    return [
      {
        id: "train-open-td",
        labelKey: "chat.suggest.openTrainerstunden",
        action: "navigate",
        route: "trainerstunden",
      },
    ];
  }
  if (isInvoiceCoachDeskMessage(assistantMsg, tools)) {
    return [
      {
        id: "train-open-billing",
        labelKey: "chat.suggest.openBilling",
        action: "navigate",
        route: TRAININGS_DESK_RECHNUNGEN_PATH,
      },
    ];
  }
  if (tools.some((tc) => tc?.name === "coachdesk.lookupPersonStatus")) {
    return [
      {
        id: "train-open-td",
        labelKey: "chat.suggest.openTrainerstunden",
        action: "navigate",
        route: "trainerstunden",
      },
    ];
  }
  return [];
}

/**
 * QuickReplies für die Off-Topic-Antwort (Scope-Guard): CTAs zu Tennis-Apps.
 * action "navigate" + route: Panel führt navigateTo(route) aus, kein LLM-Call.
 * @type {Array<{ id: string; labelKey: string; action: "navigate"; route: string }>}
 */
export const SCOPE_OFF_TOPIC_QUICK_REPLIES = [
  {
    id: "ot-schleifchen",
    labelKey: "chat.suggest.openSchleifchen",
    action: "navigate",
    route: "schleifchen-setup",
  },
  {
    id: "ot-club",
    labelKey: "chat.suggest.openClubmeisterschaft",
    action: "navigate",
    route: "clubmeisterschaft-setup",
  },
  {
    id: "ot-trainerstunden",
    labelKey: "chat.suggest.openTrainerstunden",
    action: "navigate",
    route: "trainerstunden",
  },
  {
    id: "ot-ltr",
    labelKey: "chat.suggest.openTennisLive",
    action: "navigate",
    route: "ltr",
  },
];

export { getFlow };
